var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../config';
import { TableCell, TableRow, compareQueueNames, sortQueuesByName } from '../../common';
import { ExpandableRowLabel } from '../../common/tableHierarchy';
import { useCurrencySymbol } from '../../hooks/useCurrencyOptions';
import { QueueType } from '../../multiskill/types';
import { useResourcingContext } from '../../report/hooks';
import { DisplayMode } from '../../report/resourcing';
import { useDisplayModeParams } from '../../report/selectors';
import { SEAT_EXCEEDED_COLOR, useSeatCapacity } from '../../seatCapacity';
import { FirstCell, STICKIES, isOccupancy100Percent } from '../annualTable';
import { CALCULATE_DIFF, CONSTRAINT_TYPE_COLOR, CURRENCY, DECIMAL_VALUE, HIERARCHY_LEVEL, MULTISKILL_COLOR, NEST_LEVEL, PERCENTAGES, getLobExpansionKey, getPrefixedExpansionKey, getQueueExpansionKey } from '../constants';
import { useSelectCurrency } from '../selectors';
import { LobHeaderRow } from './lobHeaderRow';
import { getCountryExpansionKey, useControlledExpansion } from './selectors';
import { getChunkBaselineValue, getExpandChildren, getGroupedMultiskillScenarioCellColor, getHighlightBorder, getScenarioCellColor, getVarianceDecimalPlaces, isGroupedMultiskillScenario } from './utils';
var getDisplayValue = function (value, keepEmpty) {
  if (keepEmpty === void 0) {
    keepEmpty = false;
  }
  return value === null && !keepEmpty ? 0 : value;
};
var findScenarioQueue = function (scenQueues, queue) {
  return scenQueues.find(function (scenarioQueue) {
    return scenarioQueue.name === queue.name && scenarioQueue.queueType === queue.queueType;
  });
};
var getScenarioQueueEntries = function (queue, perScenarioQueues) {
  return __spreadArray([queue], perScenarioQueues.map(function (scenarioQueues) {
    return findScenarioQueue(scenarioQueues, queue);
  }).filter(function (scenarioQueue) {
    return scenarioQueue !== undefined;
  }), true);
};
var isMultiskillGroupEntry = function (queue, perScenarioQueues) {
  var queueEntries = getScenarioQueueEntries(queue, perScenarioQueues);
  return queueEntries.some(function (_a) {
    var queueType = _a.queueType;
    return queueType === QueueType.Multiskill;
  });
};
var getQueueScenarioValue = function (scenQueues, queue, keyItem) {
  var _a, _b;
  return (_b = (_a = findScenarioQueue(scenQueues, queue)) === null || _a === void 0 ? void 0 : _a[keyItem]) !== null && _b !== void 0 ? _b : null;
};
var findScenarioCountry = function (scenQueues, queue, country) {
  var _a, _b;
  return (_b = (_a = findScenarioQueue(scenQueues, queue)) === null || _a === void 0 ? void 0 : _a.countries) === null || _b === void 0 ? void 0 : _b.find(function (scenarioCountry) {
    return scenarioCountry.countryName === country.countryName;
  });
};
var getCountryScenarioValue = function (scenQueues, queue, country, keyItem) {
  var _a, _b;
  return (_b = (_a = findScenarioCountry(scenQueues, queue, country)) === null || _a === void 0 ? void 0 : _a[keyItem]) !== null && _b !== void 0 ? _b : null;
};
var findScenarioLocation = function (scenQueues, queue, country, location) {
  var _a, _b;
  return (_b = (_a = findScenarioCountry(scenQueues, queue, country)) === null || _a === void 0 ? void 0 : _a.locations) === null || _b === void 0 ? void 0 : _b.find(function (scenarioLocation) {
    return scenarioLocation.locationName === location.locationName;
  });
};
var getLocationScenarioValue = function (scenQueues, queue, country, location, keyItem) {
  var _a, _b;
  return (_b = (_a = findScenarioLocation(scenQueues, queue, country, location)) === null || _a === void 0 ? void 0 : _a[keyItem]) !== null && _b !== void 0 ? _b : null;
};
var getScenarioValues = function (perScenarioQueues, queue, keyItem) {
  return perScenarioQueues.map(function (scenQueues) {
    return getQueueScenarioValue(scenQueues, queue, keyItem);
  });
};
var getScenarioCountryValues = function (queue, perScenarioQueues, keyItem) {
  var _a, _b;
  return (_b = (_a = queue.countries) === null || _a === void 0 ? void 0 : _a.map(function (country) {
    return perScenarioQueues.map(function (scenQueues) {
      return getCountryScenarioValue(scenQueues, queue, country, keyItem);
    });
  })) !== null && _b !== void 0 ? _b : [];
};
var getCountryLocationScenarioValues = function (queue, country, perScenarioQueues, keyItem) {
  var _a, _b;
  return (_b = (_a = country.locations) === null || _a === void 0 ? void 0 : _a.map(function (location) {
    return perScenarioQueues.map(function (scenQueues) {
      return getLocationScenarioValue(scenQueues, queue, country, location, keyItem);
    });
  })) !== null && _b !== void 0 ? _b : [];
};
var getScenarioLocationValues = function (queue, perScenarioQueues, keyItem) {
  var _a, _b;
  return (_b = (_a = queue.countries) === null || _a === void 0 ? void 0 : _a.map(function (country) {
    return getCountryLocationScenarioValues(queue, country, perScenarioQueues, keyItem);
  })) !== null && _b !== void 0 ? _b : [];
};
export var MultigeoLocationQueueRow = function (_a) {
  var queue = _a.queue,
    scenarioValues = _a.scenarioValues,
    data = _a.data,
    chunkSize = _a.chunkSize,
    keyItem = _a.keyItem,
    _b = _a.nestLevelOffset,
    nestLevelOffset = _b === void 0 ? 0 : _b;
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var varianceOptions = {
    decimalPlaces: getVarianceDecimalPlaces(keyItem, false)
  };
  return _jsxs(TableRow, {
    children: [_jsx(FirstCell, __assign({
      "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.locationQueue - nestLevelOffset],
      stickySizes: STICKIES,
      color: theme.palette.background.paper
    }, {
      children: _jsx("span", {
        children: queue.queueName
      })
    })), scenarioValues.map(function (value, index, arr) {
      var _a, _b;
      var baseValue = getChunkBaselineValue(scenarioValues, index, chunkSize);
      return _jsx(TableCell, {
        colSpan: 2,
        value: getDisplayValue(value),
        decimal: (_a = DECIMAL_VALUE[keyItem]) !== null && _a !== void 0 ? _a : 0,
        percentage: PERCENTAGES.includes(keyItem),
        color: getScenarioCellColor(data[index]),
        highlightBorder: index + 1 !== arr.length && !((index + 1) % chunkSize),
        expandchildren: displayMode !== DisplayMode.All && CALCULATE_DIFF.includes(keyItem) ? getExpandChildren(displayMode, baseValue !== null && baseValue !== void 0 ? baseValue : null, value, isPercentages, varianceOptions) : undefined
      }, "loc-queue-".concat((_b = queue.queueId) !== null && _b !== void 0 ? _b : queue.queueName, "-").concat(index));
    })]
  });
};
export var MultigeoLocationRow = function (_a) {
  var _b;
  var location = _a.location,
    scenarioValues = _a.scenarioValues,
    _c = _a.scenarioQueues,
    scenarioQueues = _c === void 0 ? [] : _c,
    queue = _a.queue,
    country = _a.country,
    data = _a.data,
    chunkSize = _a.chunkSize,
    keyItem = _a.keyItem,
    isMultiskillQueue = _a.isMultiskillQueue,
    group = _a.group,
    scenarioGroups = _a.scenarioGroups,
    hideLocationQueueRows = _a.hideLocationQueueRows,
    _d = _a.nestLevelOffset,
    nestLevelOffset = _d === void 0 ? 0 : _d;
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var currency = useSelectCurrency();
  var isCurrencyRow = CURRENCY.includes(keyItem);
  var _e = useCurrencySymbol(isCurrencyRow ? currency : undefined),
    currencyPrefix = _e.prefix,
    currencySuffix = _e.suffix;
  var varianceOptions = {
    decimalPlaces: getVarianceDecimalPlaces(keyItem, isCurrencyRow),
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  var isLocationExceeded = useSeatCapacity().isLocationExceeded;
  var _f = useControlledExpansion(undefined, undefined, false),
    expanded = _f.expanded,
    handleToggle = _f.handleToggle;
  var locationQueues = (_b = location.queues) !== null && _b !== void 0 ? _b : [];
  var hasQueues = !hideLocationQueueRows && !!country && locationQueues.length > 0;
  var getQueueScenarioValues = function (locationQueue) {
    return data.map(function (scenario) {
      var _a, _b, _c, _d;
      var scenarioLocation = (_b = (_a = scenario.countries) === null || _a === void 0 ? void 0 : _a.find(function (item) {
        return item.countryName === (country === null || country === void 0 ? void 0 : country.countryName);
      })) === null || _b === void 0 ? void 0 : _b.locations.find(function (item) {
        return item.locationName === location.locationName;
      });
      var scenarioQueue = (_c = scenarioLocation === null || scenarioLocation === void 0 ? void 0 : scenarioLocation.queues) === null || _c === void 0 ? void 0 : _c.find(function (item) {
        return locationQueue.queueId == null ? item.queueName === locationQueue.queueName : item.queueId === locationQueue.queueId;
      });
      return (_d = scenarioQueue === null || scenarioQueue === void 0 ? void 0 : scenarioQueue.seatsRequired) !== null && _d !== void 0 ? _d : null;
    });
  };
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.location - nestLevelOffset],
        stickySizes: STICKIES,
        color: isMultiskillQueue ? undefined : theme.palette.background.default,
        sx: {
          backgroundColor: isMultiskillQueue ? MULTISKILL_COLOR : ''
        }
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: expanded,
          onToggle: hasQueues ? handleToggle : undefined
        }, {
          children: _jsx(Stack, __assign({
            direction: "row",
            alignItems: "center",
            component: "span"
          }, {
            children: _jsx("span", {
              children: location.locationName
            })
          }))
        }))
      })), scenarioValues.map(function (value, index, arr) {
        var _a, _b, _c;
        var baseValue = getChunkBaselineValue(scenarioValues, index, chunkSize);
        var scenarioQueue = queue ? findScenarioQueue((_a = scenarioQueues[index]) !== null && _a !== void 0 ? _a : [], queue) : undefined;
        var shouldShowEmpty = keyItem === 'productiveHours' && isOccupancy100Percent(scenarioQueue === null || scenarioQueue === void 0 ? void 0 : scenarioQueue.occupancy);
        var scenarioGroup = scenarioGroups ? scenarioGroups[index] : group;
        var inScenarioGroup = !scenarioGroups || scenarioGroup !== null && scenarioGroup !== undefined;
        var effectiveGroup = inScenarioGroup ? scenarioGroup : null;
        var effectiveIsMultiskill = inScenarioGroup ? isMultiskillQueue : false;
        var useGroupedMultiskillColor = isGroupedMultiskillScenario(data[index], effectiveGroup, effectiveIsMultiskill);
        var isExceeded = keyItem === 'seatsRequired' && isLocationExceeded((_b = data[index]) === null || _b === void 0 ? void 0 : _b.id, location.locationName);
        return _jsx(TableCell, {
          colSpan: 2,
          value: getDisplayValue(shouldShowEmpty ? undefined : value, isMultiskillQueue),
          decimal: (_c = DECIMAL_VALUE[keyItem]) !== null && _c !== void 0 ? _c : 0,
          percentage: PERCENTAGES.includes(keyItem),
          currency: CURRENCY.includes(keyItem) ? currency : undefined,
          color: isExceeded ? SEAT_EXCEEDED_COLOR : getGroupedMultiskillScenarioCellColor(data[index], effectiveGroup, effectiveIsMultiskill),
          useColorValue: isExceeded || useGroupedMultiskillColor,
          highlightBorder: index + 1 !== arr.length && (index + 1) % chunkSize === 0,
          expandchildren: displayMode !== DisplayMode.All && CALCULATE_DIFF.includes(keyItem) ? getExpandChildren(displayMode, baseValue !== null && baseValue !== void 0 ? baseValue : null, value, isPercentages, varianceOptions) : undefined
        }, "loc-".concat(location.locationName, "-").concat(index));
      })]
    }), hasQueues && expanded && locationQueues.map(function (locationQueue, queueIdx) {
      var _a;
      return _jsx(MultigeoLocationQueueRow, {
        queue: locationQueue,
        scenarioValues: getQueueScenarioValues(locationQueue),
        data: data,
        chunkSize: chunkSize,
        keyItem: keyItem,
        nestLevelOffset: nestLevelOffset
      }, "".concat(location.locationName, "-").concat((_a = locationQueue.queueId) !== null && _a !== void 0 ? _a : locationQueue.queueName, "-").concat(queueIdx));
    })]
  });
};
export var MultigeoCountryRow = function (_a) {
  var country = _a.country,
    scenarioValues = _a.scenarioValues,
    scenarioLocationValues = _a.scenarioLocationValues,
    _b = _a.scenarioQueues,
    scenarioQueues = _b === void 0 ? [] : _b,
    queue = _a.queue,
    scenarioGroups = _a.scenarioGroups,
    data = _a.data,
    chunkSize = _a.chunkSize,
    keyItem = _a.keyItem,
    isMultiskillQueue = _a.isMultiskillQueue,
    group = _a.group,
    expandedProp = _a.expandedProp,
    onToggle = _a.onToggle,
    hideLocationQueueRows = _a.hideLocationQueueRows,
    _c = _a.nestLevelOffset,
    nestLevelOffset = _c === void 0 ? 0 : _c;
  var _d = useControlledExpansion(expandedProp, onToggle),
    expanded = _d.expanded,
    handleToggle = _d.handleToggle;
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var currency = useSelectCurrency();
  var isCurrencyRow = CURRENCY.includes(keyItem);
  var _e = useCurrencySymbol(isCurrencyRow ? currency : undefined),
    currencyPrefix = _e.prefix,
    currencySuffix = _e.suffix;
  var varianceOptions = {
    decimalPlaces: getVarianceDecimalPlaces(keyItem, isCurrencyRow),
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  var hasLocations = !!country.locations.length;
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.country - nestLevelOffset],
        stickySizes: STICKIES,
        sx: {
          backgroundColor: isMultiskillQueue ? MULTISKILL_COLOR : ''
        }
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: expanded,
          onToggle: hasLocations ? handleToggle : undefined,
          bold: true
        }, {
          children: country.countryName
        }))
      })), scenarioValues.map(function (value, index, arr) {
        var _a, _b;
        var baseValue = getChunkBaselineValue(scenarioValues, index, chunkSize);
        var scenarioQueue = queue ? findScenarioQueue((_a = scenarioQueues[index]) !== null && _a !== void 0 ? _a : [], queue) : undefined;
        var shouldShowEmpty = keyItem === 'productiveHours' && isOccupancy100Percent(scenarioQueue === null || scenarioQueue === void 0 ? void 0 : scenarioQueue.occupancy);
        var scenarioGroup = scenarioGroups ? scenarioGroups[index] : group;
        var inScenarioGroup = !scenarioGroups || scenarioGroup !== null && scenarioGroup !== undefined;
        var effectiveGroup = inScenarioGroup ? scenarioGroup : null;
        var effectiveIsMultiskill = inScenarioGroup ? isMultiskillQueue : false;
        var useGroupedMultiskillColor = isGroupedMultiskillScenario(data[index], effectiveGroup, effectiveIsMultiskill);
        return _jsx(TableCell, {
          colSpan: 2,
          value: getDisplayValue(shouldShowEmpty ? undefined : value, isMultiskillQueue),
          decimal: (_b = DECIMAL_VALUE[keyItem]) !== null && _b !== void 0 ? _b : 0,
          percentage: PERCENTAGES.includes(keyItem),
          currency: isCurrencyRow ? currency : undefined,
          bold: true,
          color: getGroupedMultiskillScenarioCellColor(data[index], effectiveGroup, effectiveIsMultiskill),
          useColorValue: useGroupedMultiskillColor,
          highlightBorder: index + 1 !== arr.length && !((index + 1) % chunkSize),
          expandchildren: displayMode !== DisplayMode.All && CALCULATE_DIFF.includes(keyItem) ? getExpandChildren(displayMode, baseValue !== null && baseValue !== void 0 ? baseValue : null, value, isPercentages, varianceOptions) : undefined
        }, "country-".concat(country.countryName, "-").concat(index));
      })]
    }), !!hasLocations && expanded && country.locations.map(function (loc, locIdx) {
      var _a;
      return _jsx(MultigeoLocationRow, {
        location: loc,
        scenarioValues: (_a = scenarioLocationValues[locIdx]) !== null && _a !== void 0 ? _a : scenarioValues.map(function () {
          return null;
        }),
        scenarioQueues: scenarioQueues,
        queue: queue,
        country: country,
        scenarioGroups: scenarioGroups,
        data: data,
        chunkSize: chunkSize,
        keyItem: keyItem,
        isMultiskillQueue: isMultiskillQueue,
        group: group,
        hideLocationQueueRows: hideLocationQueueRows,
        nestLevelOffset: nestLevelOffset
      }, "".concat(country.countryName, "-").concat(loc.locationName, "-").concat(locIdx));
    })]
  });
};
export var MultigeoQueueRow = function (_a) {
  var _b;
  var queue = _a.queue,
    _c = _a.scenarioQueues,
    scenarioQueues = _c === void 0 ? [] : _c,
    scenarioValues = _a.scenarioValues,
    scenarioCountryValues = _a.scenarioCountryValues,
    scenarioLocationValues = _a.scenarioLocationValues,
    data = _a.data,
    chunkSize = _a.chunkSize,
    keyItem = _a.keyItem,
    expandedProp = _a.expandedProp,
    onToggle = _a.onToggle,
    hideLocationRows = _a.hideLocationRows,
    expansionKey = _a.expansionKey,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    _d = _a.nestLevelOffset,
    nestLevelOffset = _d === void 0 ? 0 : _d;
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var currency = useSelectCurrency();
  var isCurrencyRow = CURRENCY.includes(keyItem);
  var _e = useCurrencySymbol(isCurrencyRow ? currency : undefined),
    currencyPrefix = _e.prefix,
    currencySuffix = _e.suffix;
  var varianceOptions = {
    decimalPlaces: getVarianceDecimalPlaces(keyItem, isCurrencyRow),
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  var _f = useControlledExpansion(expandedProp, onToggle),
    expanded = _f.expanded,
    handleToggle = _f.handleToggle;
  var hasCountries = !!((_b = queue.countries) === null || _b === void 0 ? void 0 : _b.length);
  var showCountryRows = hasCountries && !hideLocationRows;
  var scenarioGroups = scenarioValues.map(function (_, index) {
    var _a, _b;
    return (_b = findScenarioQueue((_a = scenarioQueues[index]) !== null && _a !== void 0 ? _a : [], queue)) === null || _b === void 0 ? void 0 : _b.group;
  });
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.queue - nestLevelOffset],
        stickySizes: STICKIES,
        sx: {
          backgroundColor: queue.queueType === QueueType.Multiskill ? MULTISKILL_COLOR : ''
        }
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: expanded,
          onToggle: showCountryRows ? handleToggle : undefined,
          bold: true
        }, {
          children: queue.name
        }))
      })), scenarioValues.map(function (value, index, arr) {
        var _a, _b;
        var baseValue = getChunkBaselineValue(scenarioValues, index, chunkSize);
        var scenarioQueue = findScenarioQueue((_a = scenarioQueues[index]) !== null && _a !== void 0 ? _a : [], queue);
        var shouldShowEmpty = keyItem === 'productiveHours' && isOccupancy100Percent(scenarioQueue === null || scenarioQueue === void 0 ? void 0 : scenarioQueue.occupancy);
        var cellValue = shouldShowEmpty ? undefined : value;
        var useGroupedMultiskillColor = isGroupedMultiskillScenario(data[index], scenarioQueue === null || scenarioQueue === void 0 ? void 0 : scenarioQueue.group, scenarioQueue === null || scenarioQueue === void 0 ? void 0 : scenarioQueue.isMultiskill);
        return _jsx(TableCell, {
          colSpan: 2,
          value: getDisplayValue(cellValue, queue.queueType === QueueType.Multiskill),
          decimal: (_b = DECIMAL_VALUE[keyItem]) !== null && _b !== void 0 ? _b : 0,
          percentage: PERCENTAGES.includes(keyItem),
          currency: isCurrencyRow ? currency : undefined,
          bold: true,
          color: getGroupedMultiskillScenarioCellColor(data[index], scenarioQueue === null || scenarioQueue === void 0 ? void 0 : scenarioQueue.group, scenarioQueue === null || scenarioQueue === void 0 ? void 0 : scenarioQueue.isMultiskill),
          useColorValue: useGroupedMultiskillColor,
          highlightBorder: getHighlightBorder(index, arr.length, chunkSize),
          expandchildren: CALCULATE_DIFF.includes(keyItem) ? getExpandChildren(displayMode, baseValue !== null && baseValue !== void 0 ? baseValue : null, value, isPercentages, varianceOptions) : undefined
        }, "queue-".concat(queue.name, "-").concat(index));
      })]
    }), showCountryRows && expanded && queue.countries.map(function (country, countryIdx) {
      var _a, _b;
      var countryExpansionKey = expansionKey ? getCountryExpansionKey(expansionKey, country.countryName) : undefined;
      return _jsx(MultigeoCountryRow, {
        country: country,
        scenarioValues: (_a = scenarioCountryValues[countryIdx]) !== null && _a !== void 0 ? _a : scenarioValues.map(function () {
          return null;
        }),
        scenarioLocationValues: (_b = scenarioLocationValues[countryIdx]) !== null && _b !== void 0 ? _b : [],
        scenarioQueues: scenarioQueues,
        queue: queue,
        scenarioGroups: scenarioGroups,
        data: data,
        chunkSize: chunkSize,
        keyItem: keyItem,
        isMultiskillQueue: queue.queueType === QueueType.Multiskill,
        group: queue.group,
        expandedProp: countryExpansionKey ? expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[countryExpansionKey] : undefined,
        onToggle: countryExpansionKey && onToggleItem ? function () {
          return onToggleItem(countryExpansionKey);
        } : undefined,
        nestLevelOffset: nestLevelOffset
      }, "".concat(queue.name, "-").concat(country.countryName, "-").concat(countryIdx));
    })]
  });
};
/** Renders the actual per-queue rows for a (possibly merged) list of queue entries. */
export var MultigeoQueueRows = function (_a) {
  var entries = _a.entries,
    lobName = _a.lobName,
    data = _a.data,
    chunkSize = _a.chunkSize,
    keyItem = _a.keyItem,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    expansionKeyPrefix = _a.expansionKeyPrefix,
    hideLocationRows = _a.hideLocationRows,
    _b = _a.nestLevelOffset,
    nestLevelOffset = _b === void 0 ? 0 : _b;
  return _jsx(_Fragment, {
    children: entries.map(function (_a) {
      var queue = _a.queue,
        constraintKey = _a.constraintKey,
        perScenarioQueues = _a.perScenarioQueues;
      var scenarioValues = getScenarioValues(perScenarioQueues, queue, keyItem);
      var scenarioCountryValues = getScenarioCountryValues(queue, perScenarioQueues, keyItem);
      var scenarioLocationValues = getScenarioLocationValues(queue, perScenarioQueues, keyItem);
      var expansionKey = getPrefixedExpansionKey(expansionKeyPrefix, getQueueExpansionKey(lobName, constraintKey, queue.name, queue.queueType));
      var allScenariosOccupancy100 = keyItem === 'productiveHours' && perScenarioQueues.every(function (scenQueues) {
        var scenQueue = findScenarioQueue(scenQueues, queue);
        return scenQueue && isOccupancy100Percent(scenQueue.occupancy);
      });
      if (allScenariosOccupancy100) {
        return null;
      }
      return _jsx(MultigeoQueueRow, {
        queue: queue,
        scenarioQueues: perScenarioQueues,
        scenarioValues: scenarioValues,
        scenarioCountryValues: scenarioCountryValues,
        scenarioLocationValues: scenarioLocationValues,
        data: data,
        chunkSize: chunkSize,
        keyItem: keyItem,
        expandedProp: expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[expansionKey],
        onToggle: onToggleItem ? function () {
          return onToggleItem(expansionKey);
        } : undefined,
        expansionKey: expansionKey,
        expandedItems: expandedItems,
        onToggleItem: onToggleItem,
        hideLocationRows: hideLocationRows,
        nestLevelOffset: nestLevelOffset
      }, expansionKey);
    })
  });
};
export var MultigeoConstraintHeaderRow = function (_a) {
  var label = _a.label,
    constraintKey = _a.constraintKey,
    lobName = _a.lobName,
    queues = _a.queues,
    perScenarioQueues = _a.perScenarioQueues,
    data = _a.data,
    chunkSize = _a.chunkSize,
    keyItem = _a.keyItem,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    expansionKeyPrefix = _a.expansionKeyPrefix,
    hideConstraintHeader = _a.hideConstraintHeader,
    hideLocationRows = _a.hideLocationRows,
    _b = _a.nestLevelOffset,
    nestLevelOffset = _b === void 0 ? 0 : _b;
  if (!queues.length) return null;
  var rowColor = CONSTRAINT_TYPE_COLOR;
  var entries = sortQueuesByName(queues).map(function (queue) {
    return {
      queue: queue,
      constraintKey: constraintKey,
      perScenarioQueues: perScenarioQueues
    };
  });
  return _jsxs(_Fragment, {
    children: [!hideConstraintHeader && _jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.queue - nestLevelOffset],
        stickySizes: STICKIES,
        color: theme.palette.primary.light
      }, {
        children: _jsx(Box, __assign({
          fontWeight: "bold"
        }, {
          children: label
        }))
      })), data.map(function (_, index, arr) {
        var _a;
        return _jsx(TableCell, {
          colSpan: 2,
          color: (_a = getScenarioCellColor(data[index])) !== null && _a !== void 0 ? _a : rowColor,
          highlightBorder: index + 1 !== arr.length && !((index + 1) % chunkSize)
        }, "def-nondef-".concat(label, "-").concat(index));
      })]
    }), _jsx(MultigeoQueueRows, {
      entries: entries,
      lobName: lobName,
      data: data,
      chunkSize: chunkSize,
      keyItem: keyItem,
      expandedItems: expandedItems,
      onToggleItem: onToggleItem,
      expansionKeyPrefix: expansionKeyPrefix,
      hideLocationRows: hideLocationRows,
      nestLevelOffset: nestLevelOffset
    })]
  });
};
var getVisibleQueues = function (queues, perScenarioQueues, keyItem, hideMultiskillQueues) {
  if (hideMultiskillQueues === void 0) {
    hideMultiskillQueues = false;
  }
  var visibleQueues = hideMultiskillQueues ? queues.filter(function (queue) {
    return !isMultiskillGroupEntry(queue, perScenarioQueues);
  }) : queues;
  if (keyItem !== 'productiveHours') return visibleQueues;
  return visibleQueues.filter(function (queue) {
    var allScenariosOccupancy100 = perScenarioQueues.every(function (scenQueues) {
      var scenQueue = findScenarioQueue(scenQueues, queue);
      return scenQueue && isOccupancy100Percent(scenQueue.occupancy);
    });
    return !allScenariosOccupancy100;
  });
};
export var MultigeoLobRow = function (_a) {
  var lob = _a.lob,
    perScenarioLob = _a.perScenarioLob,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    data = _a.data,
    chunkSize = _a.chunkSize,
    keyItem = _a.keyItem,
    expansionKeyPrefix = _a.expansionKeyPrefix,
    hideConstraintHeader = _a.hideConstraintHeader,
    hideLocationRows = _a.hideLocationRows,
    hideMultiskillQueues = _a.hideMultiskillQueues,
    _b = _a.nestLevelOffset,
    nestLevelOffset = _b === void 0 ? 0 : _b;
  var t = useTranslation('report', {
    keyPrefix: 'annual'
  }).t;
  var flatPerScenario = React.useMemo(function () {
    return perScenarioLob.flat();
  }, [perScenarioLob]);
  var perScenarioDefQueues = flatPerScenario.map(function (s) {
    return s.defQueues;
  });
  var perScenarioNonDefQueues = flatPerScenario.map(function (s) {
    return s.nonDefQueues;
  });
  // Merged, alphanumerically-sorted view used when the Deferrable/Non-Deferrable
  // headers are hidden, so the queue list reads as one flat A-Z list instead of
  // all deferrable queues followed by all non-deferrable ones.
  var displayLobName = lob.lobName === '' ? t('unassignedLob') : lob.lobName;
  var expansionKey = getPrefixedExpansionKey(expansionKeyPrefix, getLobExpansionKey(lob.lobName));
  var _c = useControlledExpansion(expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[expansionKey], onToggleItem ? function () {
      return onToggleItem(expansionKey);
    } : undefined),
    expanded = _c.expanded,
    handleToggle = _c.handleToggle;
  var visibleDefQueues = getVisibleQueues(lob.defQueues, perScenarioDefQueues, keyItem, hideMultiskillQueues);
  var visibleNonDefQueues = getVisibleQueues(lob.nonDefQueues, perScenarioNonDefQueues, keyItem, hideMultiskillQueues);
  var mergedQueueEntries = __spreadArray(__spreadArray([], visibleDefQueues.map(function (queue) {
    return {
      queue: queue,
      constraintKey: 'deferrable',
      perScenarioQueues: perScenarioDefQueues
    };
  }), true), visibleNonDefQueues.map(function (queue) {
    return {
      queue: queue,
      constraintKey: 'nonDeferrable',
      perScenarioQueues: perScenarioNonDefQueues
    };
  }), true).sort(function (a, b) {
    return compareQueueNames(a.queue.name, b.queue.name);
  });
  var hasVisibleQueues = visibleDefQueues.length > 0 || visibleNonDefQueues.length > 0;
  if (!hasVisibleQueues) return null;
  return _jsxs(_Fragment, {
    children: [_jsx(LobHeaderRow, {
      displayName: displayLobName,
      data: data,
      chunkSize: chunkSize,
      backgroundColor: theme.palette.grey[400],
      expanded: expanded,
      onToggle: handleToggle,
      nestLevel: HIERARCHY_LEVEL.lob - nestLevelOffset
    }), expanded && (hideConstraintHeader ? _jsx(MultigeoQueueRows, {
      entries: mergedQueueEntries,
      lobName: lob.lobName,
      data: data,
      chunkSize: chunkSize,
      keyItem: keyItem,
      expandedItems: expandedItems,
      onToggleItem: onToggleItem,
      expansionKeyPrefix: expansionKeyPrefix,
      hideLocationRows: hideLocationRows,
      nestLevelOffset: nestLevelOffset
    }) : _jsxs(_Fragment, {
      children: [_jsx(MultigeoConstraintHeaderRow, {
        label: t('deferrable'),
        constraintKey: "deferrable",
        lobName: lob.lobName,
        queues: visibleDefQueues,
        perScenarioQueues: perScenarioDefQueues,
        data: data,
        chunkSize: chunkSize,
        keyItem: keyItem,
        expandedItems: expandedItems,
        onToggleItem: onToggleItem,
        expansionKeyPrefix: expansionKeyPrefix,
        hideConstraintHeader: hideConstraintHeader,
        hideLocationRows: hideLocationRows,
        nestLevelOffset: nestLevelOffset
      }), _jsx(MultigeoConstraintHeaderRow, {
        label: t('nonDeferrable'),
        constraintKey: "nonDeferrable",
        lobName: lob.lobName,
        queues: visibleNonDefQueues,
        perScenarioQueues: perScenarioNonDefQueues,
        data: data,
        chunkSize: chunkSize,
        keyItem: keyItem,
        expandedItems: expandedItems,
        onToggleItem: onToggleItem,
        expansionKeyPrefix: expansionKeyPrefix,
        hideConstraintHeader: hideConstraintHeader,
        hideLocationRows: hideLocationRows,
        nestLevelOffset: nestLevelOffset
      })]
    }))]
  });
};